import React, { useEffect, useState } from 'react';
import { X, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { formatApiError } from '@/utils/apiError';
import { ProBadge } from '@/components/billing/UpgradeModal';

export function CustomerTextModal({ blockId, onUpgrade, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!blockId) return;
    fetch(`${API_URL}/api/schedule/blocks/${blockId}/customer-text`, { credentials: 'include' })
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(formatApiError(d)); setData(d); setBody(d.body); })
      .catch((e) => { toast.error(e.message); onClose(); });
  }, [blockId, onClose]);

  if (!blockId || !data) return null;

  const send = async () => {
    if (!data.allowed) { onUpgrade(); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/schedule/blocks/${blockId}/customer-text`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: body }) });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d));
      toast.success(`Text sent to ${data.customer_name}`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[55] sm:p-4" data-testid="customer-text-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-bold text-gray-900 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#006400]" /> Text {data.customer_name} their dates {!data.allowed && <ProBadge />}</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close" data-testid="customer-text-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-xs text-gray-500">To: <span className="font-semibold text-gray-800" data-testid="customer-text-to">{data.to || 'no phone number on this project'}</span>{data.last_sent_at && <span className="ml-2 text-emerald-700">· last sent {new Date(data.last_sent_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}</div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={600} className="w-full border-2 rounded-lg p-3 text-sm" data-testid="customer-text-body" />
          <div className="text-[11px] text-gray-400 text-right">{body.length}/600</div>
        </div>
        <div className="px-5 py-3 border-t flex gap-2" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}>
          <Button variant="outline" onClick={onClose} className="flex-1" data-testid="customer-text-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy || (data.allowed && !data.to)} className="flex-1 bg-[#006400] hover:bg-[#004d00] text-white" data-testid="customer-text-send">
            <Send className="w-4 h-4 mr-1.5" /> {busy ? 'Sending…' : data.allowed ? 'Send text' : 'Upgrade to send'}
          </Button>
        </div>
      </div>
    </div>
  );
}
